import { useCallback, useEffect, useState } from 'react';

// 'parent' = item de niveau 1 (ex: client), 'child' = item de niveau 2 (ex: contrat)
export type SelectionType = 'parent' | 'child';

// Clé composite : `${columnId}||${type}||${itemId}`
type SelectionKey = string;

export function useSelectionState() {
  const [selection, setSelection] = useState<Set<SelectionKey>>(new Set());

  const makeKey = (columnId: string, type: SelectionType, itemId: number): SelectionKey =>
    `${columnId}||${type}||${itemId}`;

  const parseKey = useCallback((key: SelectionKey) => {
    const firstSep = key.indexOf('||');
    const secondSep = key.indexOf('||', firstSep + 2);
    return {
      columnId: key.slice(0, firstSep),
      type: key.slice(firstSep + 2, secondSep) as SelectionType,
      itemId: Number(key.slice(secondSep + 2))
    };
  }, []);

  const toggleSelect = useCallback((columnId: string, type: SelectionType, itemId: number) => {
    const key = makeKey(columnId, type, itemId);
    setSelection((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  const isSelected = useCallback(
    (columnId: string, type: SelectionType, itemId: number) =>
      selection.has(makeKey(columnId, type, itemId)),
    [selection]
  );

  const clearSelection = useCallback(() => setSelection(new Set()), []);

  const removeMany = useCallback(
    (items: { columnId: string; type: SelectionType; itemId: number }[]) => {
      setSelection((prev) => {
        const next = new Set(prev);
        items.forEach(({ columnId, type, itemId }) => next.delete(makeKey(columnId, type, itemId)));
        return next;
      });
    },
    []
  );

  const addMany = useCallback(
    (items: { columnId: string; type: SelectionType; itemId: number }[]) => {
      setSelection((prev) => {
        const next = new Set(prev);
        items.forEach(({ columnId, type, itemId }) => next.add(makeKey(columnId, type, itemId)));
        return next;
      });
    },
    []
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearSelection();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [clearSelection]);

  return { selection, toggleSelect, isSelected, clearSelection, parseKey, removeMany, addMany };
}
